// ============================================
// COMPONENT: FaultHistoryView
// ============================================
// Archivo: src/components/fault/FaultHistoryView.jsx

import React, { useState, useMemo } from 'react';
import {
  ArrowLeft,
  Download,
  Filter,
  AlertTriangle,
  Clock,
  DollarSign,
  Eye,
  LogOut,
  UserCog
} from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { formatCLP } from '../../utils/formatters';
import { formatTipoFalla, formatEstadoFalla, formatPrioridad } from '../../utils/faultFormatters';

const CHART_COLORS = ['#00d4ff', '#ff3b5c', '#00e676', '#ffb300', '#a78bfa', '#fb923c'];

const FaultHistoryView = ({ 
  faults = [],
  plants = [],
  equipments = [],
  user,
  onBack,
  onLogout,
  onOpenMyData,
  onViewFault
}) => {
  const [filters, setFilters] = useState({
    plantId: '',
    estado: '',
    prioridad: '',
    startDate: '',
    endDate: '',
    searchTerm: ''
  });

  const [showFilters, setShowFilters] = useState(false);

  // Filtrar fallas
  const filteredFaults = useMemo(() => {
    return faults.filter(fault => {
      // Filtro por planta
      if (filters.plantId && fault.plant_id !== parseInt(filters.plantId)) {
        return false;
      }

      // Filtro por estado
      if (filters.estado && fault.estado !== filters.estado) {
        return false;
      }

      // Filtro por prioridad
      if (filters.prioridad && fault.prioridad !== filters.prioridad) {
        return false;
      }

      // Filtro por fecha inicio
      if (filters.startDate && fault.fecha_reporte < filters.startDate) {
        return false;
      }

      // Filtro por fecha fin
      if (filters.endDate && fault.fecha_reporte > filters.endDate) {
        return false;
      }

      // Búsqueda por texto
      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        const matchDescription = fault.descripcion?.toLowerCase().includes(term);
        const matchEquipment = fault.equipment_name?.toLowerCase().includes(term);
        const matchPlant = fault.plant_name?.toLowerCase().includes(term);
        return matchDescription || matchEquipment || matchPlant;
      }

      return true;
    });
  }, [faults, filters]);

 

  // Estadísticas de fallas filtradas
  const stats = useMemo(() => {
    const total = filteredFaults.length;
    const resueltas = filteredFaults.filter(f => f.estado === 'resuelta').length;
    const activas = total - resueltas;
    
     const costoTotal = filteredFaults.reduce((sum, f) => 
    sum + (parseFloat(f.costo_total) || 0) ,0
    );

    const tiempoTotal = filteredFaults.reduce((sum, f) => 
      sum + (parseInt(f.tiempo_total_inactividad) || 0), 0
    );

    return {
      total,
      activas,
      resueltas,
      costoTotal,
      tiempoTotal: Math.round(tiempoTotal / 60) // horas
    };
  }, [filteredFaults]);

  // Fallas por mes (últimos 6 meses)
  const chartFaultsByMonth = useMemo(() => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      months.push({
        mes: d.toLocaleDateString('es-CL', { month: 'short', year: '2-digit' }),
        year: d.getFullYear(),
        month: d.getMonth(),
        cantidad: 0
      });
    }
    filteredFaults.forEach(f => {
      const d = new Date(f.fecha_reporte);
      const m = months.find(mo => mo.year === d.getFullYear() && mo.month === d.getMonth());
      if (m) m.cantidad++;
    });
    return months;
  }, [filteredFaults]);

  // Fallas por tipo
  const chartFaultsByType = useMemo(() => {
    const counts = {};
    filteredFaults.forEach(f => {
      const tipo = formatTipoFalla(f.tipo_falla) || 'Sin tipo';
      counts[tipo] = (counts[tipo] || 0) + 1;
    });
    return Object.entries(counts).map(([tipo, cantidad]) => ({ tipo, cantidad }));
  }, [filteredFaults]);

  // Top 5 plantas con más fallas
  const chartFaultsByPlant = useMemo(() => {
    const counts = {};
    filteredFaults.forEach(f => {
      const plant = f.plant_name || 'Sin planta';
      counts[plant] = (counts[plant] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([planta, cantidad]) => ({ planta, cantidad }))
      .sort((a, b) => b.cantidad - a.cantidad)
      .slice(0, 5);
  }, [filteredFaults]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      plantId: '',
      estado: '',
      prioridad: '',
      startDate: '',
      endDate: '',
      searchTerm: ''
    });
  };

  const exportToExcel = () => {
    if (filteredFaults.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    // Preparar datos para Excel
    const data = filteredFaults.map(fault => ({
      'ID': fault.id,
      'Fecha Reporte': new Date(fault.fecha_reporte).toLocaleDateString('es-CL'),
      'Hora Inicio': fault.hora_inicio ? new Date(fault.hora_inicio).toLocaleTimeString('es-CL') : '',
      'Planta': fault.plant_name || '',
      'Equipo': fault.equipment_name || 'General',
      'Tipo Falla': formatTipoFalla(fault.tipo_falla),
      'Descripción': fault.descripcion || '',
      'Prioridad': formatPrioridad(fault.prioridad),
      'Impacto': fault.impacto || '',
      'Estado': formatEstadoFalla(fault.estado),
      'Responsable Reporte': fault.responsable_reporte || '',
      'Acción Inmediata': fault.accion_inmediata || '',
      'Fecha Solución': fault.fecha_solucion ? new Date(fault.fecha_solucion).toLocaleDateString('es-CL') : '',
      'Hora Fin': fault.hora_fin ? new Date(fault.hora_fin).toLocaleTimeString('es-CL') : '',
      'Tiempo Inactividad (min)': fault.tiempo_total_inactividad || 0,
      'Solución': fault.solucion || '',
      'Responsable Solución': fault.responsable_solucion || '',
      'Costo Reparación': fault.costo_reparacion || 0,
      'Costo Repuestos': fault.costo_repuestos || 0,
      'Costo Mano Obra': fault.costo_mano_obra || 0,
      'Costo Total': fault.costo_total || 0,
      'Número OC': fault.numero_oc || '',
      'Fecha OC': fault.fecha_oc || '',
      'Monto OC': fault.monto_oc || 0,
      'Proveedor': fault.proveedor || '',
      'Quien Aprueba OC': fault.quien_aprueba_oc || '',
      'Estado OC': fault.estado_oc || ''
    }));

    // Crear worksheet
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Ajustar ancho de columnas
    const colWidths = [
      { wch: 5 },  // ID
      { wch: 12 }, // Fecha Reporte
      { wch: 10 }, // Hora Inicio
      { wch: 20 }, // Planta
      { wch: 20 }, // Equipo
      { wch: 15 }, // Tipo Falla
      { wch: 50 }, // Descripción
      { wch: 10 }, // Prioridad
      { wch: 10 }, // Impacto
      { wch: 15 }, // Estado
      { wch: 20 }, // Responsable Reporte
      { wch: 40 }, // Acción Inmediata
      { wch: 12 }, // Fecha Solución
      { wch: 10 }, // Hora Fin
      { wch: 20 }, // Tiempo Inactividad
      { wch: 40 }, // Solución
      { wch: 20 }, // Responsable Solución
      { wch: 15 }, // Costo Reparación
      { wch: 15 }, // Costo Repuestos
      { wch: 15 }, // Costo Mano Obra
      { wch: 15 }, // Costo Total
      { wch: 15 }, // Número OC
      { wch: 12 }, // Fecha OC
      { wch: 15 }, // Monto OC
      { wch: 20 }, // Proveedor
      { wch: 20 }, // Quien Aprueba OC
      { wch: 15 }  // Estado OC
    ];
    ws['!cols'] = colWidths;

    // Crear workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Historial Fallas');

    // Generar archivo
    const filename = `Historial_Fallas_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'reportada': return 'bg-red-500';
      case 'en_revision': return 'bg-yellow-500';
      case 'esperando_repuesto': return 'bg-orange-500';
      case 'resuelta': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getPrioridadColor = (prioridad) => {
    switch (prioridad) {
      case 'urgente': return 'text-red-600';
      case 'alta': return 'text-orange-600';
      case 'media': return 'text-yellow-600';
      case 'baja': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Navigation */}
      <nav className="bg-slate-800 px-6 py-4 border-b border-slate-700">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-6">
            <button
              onClick={onBack}
              className="text-cyan-400 hover:text-cyan-300"
            >
              <ArrowLeft size={24} />
            </button>
            <AlertTriangle size={32} className="text-red-400" />
            <div className="text-white">
              <h1 className="text-xl font-bold">Historial de Fallas</h1>
              <p className="text-xs text-slate-400">
                Sistema de Gestión de Fallas PRT
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <p className="text-sm text-cyan-400">
              {new Date().toLocaleString('es-CL')}
            </p>
            {onOpenMyData && (
              <button onClick={onOpenMyData} title="Mis Datos" className="p-2 text-slate-300 hover:text-white flex items-center gap-1 text-sm">
                <UserCog size={15} /><span className="hidden sm:inline">Mis Datos</span>
              </button>
            )}
            <button
              onClick={onLogout}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="p-6">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-5 gap-4 mb-6">
          <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
            <p className="text-slate-400 text-sm mb-1">Total Fallas</p>
            <p className="text-3xl font-bold text-white">{stats.total}</p>
          </div>
          
          <div className="bg-slate-800 p-4 rounded-lg border border-red-500">
            <p className="text-slate-400 text-sm mb-1">Activas</p>
            <p className="text-3xl font-bold text-red-500">{stats.activas}</p>
          </div>
          
          <div className="bg-slate-800 p-4 rounded-lg border border-green-500">
            <p className="text-slate-400 text-sm mb-1">Resueltas</p>
            <p className="text-3xl font-bold text-green-500">{stats.resueltas}</p>
          </div>
          
          <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign size={16} className="text-slate-400" />
              <p className="text-slate-400 text-sm">Costo Total</p>
            </div>
            <p className="text-2xl font-bold text-white">
              {formatCLP(stats.costoTotal)}  
            </p>
          </div>
          
          <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
            <div className="flex items-center gap-2 mb-1">
              <Clock size={16} className="text-slate-400" />
              <p className="text-slate-400 text-sm">Tiempo Total</p>
            </div>
            <p className="text-2xl font-bold text-white">{stats.tiempoTotal}h</p>
          </div>
        </div>

        {/* Gráficos */}
        {filteredFaults.length > 0 && (
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {/* Fallas por mes */}
            <div className="bg-[#0d1826] p-4 rounded-lg border border-slate-700">
              <h3 className="text-white text-sm font-semibold mb-3">Fallas por Mes</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartFaultsByMonth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                  <XAxis dataKey="mes" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', color: '#fff' }} />
                  <Bar dataKey="cantidad" fill="#00d4ff" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Fallas por tipo */}
            <div className="bg-[#0d1826] p-4 rounded-lg border border-slate-700">
              <h3 className="text-white text-sm font-semibold mb-3">Fallas por Tipo</h3>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={chartFaultsByType}
                    dataKey="cantidad"
                    nameKey="tipo"
                    cx="50%"
                    cy="45%"
                    outerRadius={60}
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {chartFaultsByType.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', color: '#fff' }} />
                  <Legend wrapperStyle={{ fontSize: 10, color: '#94a3b8' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Top 5 plantas */}
            <div className="bg-[#0d1826] p-4 rounded-lg border border-slate-700">
              <h3 className="text-white text-sm font-semibold mb-3">Top 5 Plantas</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartFaultsByPlant} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                  <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="planta" tick={{ fill: '#94a3b8', fontSize: 10 }} width={80} />
                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', color: '#fff' }} />
                  <Bar dataKey="cantidad" fill="#ff3b5c" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="bg-slate-800 p-4 rounded-lg border border-slate-700 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-white font-semibold">Filtros y Acciones</h2>
            <div className="flex gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-4 py-2 bg-slate-700 text-white rounded hover:bg-slate-600 flex items-center gap-2"
              >
                <Filter size={16} />
                {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
              </button>
              <button
                onClick={exportToExcel}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2"
              >
                <Download size={16} />
                Exportar a Excel
              </button>
            </div>
          </div>

          {/* Filtros */}
          {showFilters && (
            <div className="grid md:grid-cols-4 gap-4 pt-4 border-t border-slate-700">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Planta</label>
                <select
                  value={filters.plantId}
                  onChange={(e) => handleFilterChange('plantId', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                >
                  <option value="">Todas las plantas</option>
                  {plants.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Estado</label>
                <select
                  value={filters.estado}
                  onChange={(e) => handleFilterChange('estado', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                >
                  <option value="">Todos</option>
                  <option value="reportada">Reportada</option>
                  <option value="en_revision">En Revisión</option>
                  <option value="esperando_repuesto">Esperando Repuesto</option>
                  <option value="resuelta">Resuelta</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Prioridad</label>
                <select
                  value={filters.prioridad}
                  onChange={(e) => handleFilterChange('prioridad', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                >
                  <option value="">Todas</option>
                  <option value="baja">Baja</option>
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Buscar</label>
                <input
                  type="text"
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  placeholder="Descripción, equipo..."
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm placeholder-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Fecha Inicio</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Fecha Fin</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                />
              </div>

              <div className="md:col-span-2 flex items-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-slate-700 text-slate-300 rounded hover:bg-slate-600 text-sm"
                >
                  Limpiar Filtros
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Tabla de Fallas */}
        <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
          {filteredFaults.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle size={48} className="mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">No se encontraron fallas</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Planta</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Equipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Descripción</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Prioridad</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Costo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Tiempo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {filteredFaults.map(fault => (
                    <tr key={fault.id} className="hover:bg-slate-750">
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {new Date(fault.fecha_reporte).toLocaleDateString('es-CL')}
                      </td>
                      <td className="px-4 py-3 text-sm text-white font-medium">
                        {fault.plant_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {fault.equipment_name || 'General'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {formatTipoFalla(fault.tipo_falla)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300 max-w-xs truncate">
                        {fault.descripcion}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`font-semibold ${getPrioridadColor(fault.prioridad)}`}>
                          {formatPrioridad(fault.prioridad).toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold text-white ${getEstadoColor(fault.estado)}`}>
                          {formatEstadoFalla(fault.estado)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {formatCLP(fault.costo_total)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {Math.round((fault.tiempo_total_inactividad || 0) / 60)}h
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => onViewFault(fault)}
                          className="p-2 text-blue-400 hover:text-blue-300"
                          title="Ver detalles"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info de resultados */}
        <div className="mt-4 text-center text-sm text-slate-400">
          Mostrando {filteredFaults.length} de {faults.length} fallas
        </div>
      </div>
    </div>
  );
};

export default FaultHistoryView;
